import { useTranslation as useI18nTranslation } from 'react-i18next';
import { useCurrentLanguage, useLanguageActions } from '@/store/languageStore';

/**
 * Custom Translation Hook
 * 
 * Enhanced wrapper around react-i18next with additional functionality
 * for the ALZ Token Portal
 */
export const useTranslation = (namespace: string = 'common') => {
  const { t, i18n } = useI18nTranslation(namespace);
  const currentLanguage = useCurrentLanguage();
  const { setLanguage, toggleLanguage } = useLanguageActions();
  
  return {
    t,
    i18n,
    currentLanguage,
    setLanguage,
    toggleLanguage,
    isPortuguese: currentLanguage === 'pt',
    isEnglish: currentLanguage === 'en',
  };
};

/**
 * Format date according to current language
 */
export const useLocalizedDate = () => {
  const { currentLanguage } = useTranslation();
  
  const formatDate = (date: string | Date, options?: Intl.DateTimeFormatOptions) => {
    const locale = currentLanguage === 'pt' ? 'pt-BR' : 'en-US';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
    };
    
    return dateObj.toLocaleDateString(locale, { ...defaultOptions, ...options });
  };
  
  const formatDateTime = (date: string | Date) => {
    return formatDate(date, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  const formatRelativeTime = (date: string | Date) => {
    const locale = currentLanguage === 'pt' ? 'pt-BR' : 'en-US';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    
    const diffInMs = dateObj.getTime() - new Date().getTime();
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
    
    if (Math.abs(diffInDays) < 1) {
      const diffInHours = Math.round(diffInMs / (1000 * 60 * 60));
      if (Math.abs(diffInHours) < 1) {
        const diffInMinutes = Math.round(diffInMs / (1000 * 60));
        return rtf.format(diffInMinutes, 'minute');
      }
      return rtf.format(diffInHours, 'hour');
    }
    
    return rtf.format(diffInDays, 'day');
  };
  
  return {
    formatDate,
    formatDateTime,
    formatRelativeTime,
    currentLanguage,
  };
};